import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { ScrollToTopOnNavigate } from './components/PageTransition';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { NotificationProvider } from './context/NotificationContext';
import Navbar from './components/Navbar';
import BottomNav from './components/BottomNav';
import Hero from './components/Hero';
import Services from './components/Services';
import Products from './components/Products';
import Dashboard from './components/Dashboard';
import AdminPanel from './components/AdminPanel';
import ProductDetail from './components/ProductDetail';
import HelpCenter from './components/HelpCenter';
import Sitemap from './components/Sitemap';
import TermsAndConditions from './components/TermsAndConditions';
import PrivacyPolicy from './components/PrivacyPolicy';
import HomeSlider from './components/HomeSlider';
import HomeStats from './components/HomeStats';
import Articles from './components/Articles';
import ArticleDetail from './components/ArticleDetail';
import Regulations from './components/Regulations';
import ConstructionEducation from './components/ConstructionEducation';
import EducationDetail from './components/EducationDetail';
import ServiceDetail from './components/ServiceDetail';
import PhoneRegistrationModal from './components/PhoneRegistrationModal';
import PromoPopupModal from './components/PromoPopupModal';
import SiteLogo from './components/SiteLogo';
import FloatingChatAdmin from './components/FloatingChatAdmin';
import FAQAccordion from './components/FAQAccordion';
import DynamicFavicon from './components/DynamicFavicon';
import HomeNewsSection from './components/HomeNewsSection';
import BackToTop from './components/BackToTop';
import { Toaster } from '@/components/ui/sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  BookOpen, 
  ShieldCheck, 
  FileText, 
  Users,
  Mail, 
  MapPin, 
  MessageCircle, 
  ArrowUpRight,
  Instagram,
  Facebook,
  Music,
  Youtube,
  Calendar,
  Star
} from 'lucide-react';
import { db, collection, query, where, onSnapshot, orderBy } from './firebase';

function Home() {
  return (
    <>
      {/* PromoPopupModal sementara dinonaktifkan sesuai permintaan */}
      {/* <PromoPopupModal /> */}
      <HomeSlider />
      <HomeStats />
      
      {/* Section Services */}
      <motion.div 
        id="services" 
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-50px' }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] as const }}
        className="pt-24 pb-12"
      >
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-40px' }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] as const }}
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16 text-center"
        >
          <div className="max-w-3xl mx-auto">
            <span className="text-gold font-black uppercase tracking-[0.3em] text-sm mb-4 block">Solusi Unggulan</span>
            <h2 className="text-4xl md:text-5xl font-black text-foreground leading-tight mb-6">
              Layanan <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold to-gold-light">Konstruksi</span> Profesional
            </h2>
            <p className="text-foreground/60 font-medium text-lg mx-auto max-w-xl">
              Mulai dari perizinan hingga dokumen teknis, kami siap membantu kesuksesan proyek Anda.
            </p>
          </div>
        </motion.div>
        <Services limit={3} />
      </motion.div>

      {/* Section Products */}
      <motion.div 
        id="products" 
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-50px' }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] as const }}
        className="py-24 bg-muted/30"
      >
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-40px' }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] as const }}
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12 text-center"
        >
          <h2 className="text-3xl md:text-4xl font-black text-foreground mb-4">Katalog <span className="text-gold">Produk Digital</span></h2>
          <p className="text-foreground/60 max-w-2xl mx-auto">Template dokumen dan software pendukung yang siap pakai untuk mempercepat pekerjaan Anda.</p>
        </motion.div>
        <Products limit={3} hideFilters={true} />
        
        {/* In-page Promo Banner for Document Formats */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] as const }}
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12"
        >
          <div className="relative rounded-3xl overflow-hidden bg-gradient-to-r from-navy via-slate-900 to-navy border border-gold/30 p-6 sm:p-10 shadow-2xl text-white flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="absolute top-0 right-0 w-80 h-80 bg-gold/10 rounded-full blur-3xl pointer-events-none" />
            <div className="space-y-3 text-center lg:text-left z-10 max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gold/20 border border-gold/40 text-gold text-xs font-black uppercase tracking-wider">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gold opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-gold"></span>
                </span>
                Promo Format Dokumen Siap Pakai
              </div>
              <h3 className="text-2xl sm:text-3xl font-black tracking-tight text-white leading-tight">
                Butuh Template Dokumen & Administrasi <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold to-gold-light">Tender Konstruksi?</span>
              </h3>
              <p className="text-white/70 text-sm leading-relaxed">
                Dapatkan paket lengkap SMKK, RKK, RAB, Metode Kerja, dan SOP standar PUPR. Konsultasikan kebutuhan format dokumen Anda langsung melalui WhatsApp.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4 z-10 shrink-0 w-full lg:w-auto">
              <a
                href="https://wa.me/6281261089400?text=Halo%20Admin%20Azman204,%20saya%20tertarik%20dengan%20Promo%20Format%20Dokumen%20Konstruksi%20lengkap.%20Mohon%20info%20katalog%20dan%20penawarannya."
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-7 py-4 rounded-2xl shadow-xl shadow-emerald-600/25 hover:scale-105 transition-all text-sm"
              >
                <MessageCircle className="h-5 w-5 fill-current" />
                <span>Hubungi WA : 0812-6108-9400</span>
              </a>
              <Button asChild variant="outline" className="w-full sm:w-auto border-white/20 text-white hover:bg-white/10 rounded-2xl px-6 py-6 font-bold text-sm">
                <Link to="/products">Lihat Semua Produk</Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Section Education / Pusat Belajar Konstruksi */}
      <motion.div 
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-50px' }}
        transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] as const }}
        className="py-24 bg-navy relative overflow-hidden"
      >
        <div className="absolute top-0 right-0 w-96 h-96 bg-gold/5 rounded-full -mr-48 -mt-48 blur-3xl"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] as const }}
            >
              <span className="text-gold font-black uppercase tracking-[0.3em] text-sm mb-6 block">Pusat Belajar Konstruksi</span>
              <h2 className="text-4xl md:text-5xl font-black text-white leading-tight mb-8">
                Pahami <span className="text-gold">Regulasi & Teknis</span> Dengan Benar
              </h2>
              <p className="text-white/60 text-lg mb-10 leading-relaxed max-w-xl font-medium">
                Kami menyediakan modul edukasi terstruktur dan glosarium istilah konstruksi untuk membantu Anda memahami standar industri jasa konstruksi Indonesia.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button asChild size="lg" className="bg-gold text-navy hover:bg-gold-light font-bold rounded-xl px-8 h-14 shadow-lg shadow-gold/20 hover:scale-105 transition-all">
                  <Link to="/education">Mulai Belajar</Link>
                </Button>
                <div className="flex -space-x-3">
                  {[1, 2, 3, 4].map(i => (
                    <div key={i} className="w-12 h-12 rounded-full border-4 border-navy bg-slate-800 flex items-center justify-center overflow-hidden">
                      <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${i + 10}`} alt="User" />
                    </div>
                  ))}
                  <div className="w-12 h-12 rounded-full border-4 border-navy bg-gold flex items-center justify-center text-navy font-black text-[10px]">
                    +500
                  </div>
                </div>
              </div>
            </motion.div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-30px' }}
                  transition={{ duration: 0.5, delay: 0.1, ease: [0.22, 1, 0.36, 1] as const }}
                  whileHover={{ y: -4, transition: { duration: 0.2 } }}
                  className="bg-white/5 backdrop-blur-sm p-6 rounded-3xl border border-white/10 hover:border-gold/30 transition-all cursor-pointer"
                >
                  <div className="h-10 w-10 bg-gold/10 rounded-xl flex items-center justify-center text-gold mb-4">
                    <BookOpen className="h-6 w-6" />
                  </div>
                  <h4 className="text-white font-bold mb-2">Modul Teknis</h4>
                  <p className="text-white/40 text-xs">Standar SNI & PU terbaru.</p>
                </motion.div>
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-30px' }}
                  transition={{ duration: 0.5, delay: 0.2, ease: [0.22, 1, 0.36, 1] as const }}
                  whileHover={{ y: -4, transition: { duration: 0.2 } }}
                  className="bg-white/5 backdrop-blur-sm p-6 rounded-3xl border border-white/10 hover:border-gold/30 transition-all cursor-pointer"
                >
                  <div className="h-10 w-10 bg-blue-500/10 rounded-xl flex items-center justify-center text-blue-500 mb-4">
                    <ShieldCheck className="h-6 w-6" />
                  </div>
                  <h4 className="text-white font-bold mb-2">K3 Proyek</h4>
                  <p className="text-white/40 text-xs">Keselamatan kerja hulu hilir.</p>
                </motion.div>
              </div>
              <div className="space-y-4 pt-8">
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-30px' }}
                  transition={{ duration: 0.5, delay: 0.3, ease: [0.22, 1, 0.36, 1] as const }}
                  whileHover={{ y: -4, transition: { duration: 0.2 } }}
                  className="bg-white/5 backdrop-blur-sm p-6 rounded-3xl border border-white/10 hover:border-gold/30 transition-all cursor-pointer"
                >
                  <div className="h-10 w-10 bg-emerald-500/10 rounded-xl flex items-center justify-center text-emerald-500 mb-4">
                    <FileText className="h-6 w-6" />
                  </div>
                  <h4 className="text-white font-bold mb-2">Glosarium</h4>
                  <p className="text-white/40 text-xs">Istilah teknis terlengkap.</p>
                </motion.div>
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-30px' }}
                  transition={{ duration: 0.5, delay: 0.4, ease: [0.22, 1, 0.36, 1] as const }}
                  whileHover={{ y: -4, transition: { duration: 0.2 } }}
                  className="bg-white/5 backdrop-blur-sm p-6 rounded-3xl border border-white/10 hover:border-gold/30 transition-all cursor-pointer"
                >
                  <div className="h-10 w-10 bg-purple-500/10 rounded-xl flex items-center justify-center text-purple-500 mb-4">
                    <Users className="h-6 w-6" />
                  </div>
                  <h4 className="text-white font-bold mb-2">Manajemen</h4>
                  <p className="text-white/40 text-xs">Tata kelola administrasi.</p>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
      
      <HomeNewsSection />
      <FAQAccordion />
      <Testimonials />
      <Footer />
    </>
  );
}

interface UserReview {
  id: string;
  userName: string;
  userEmail?: string;
  productName?: string;
  rating: number;
  reviewText: string;
  createdAt?: any;
}

function Testimonials() {
  const [reviews, setReviews] = React.useState<UserReview[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const q = query(
      collection(db, 'productReviews'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const reviewsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as UserReview));
      setReviews(reviewsData);
      setLoading(false);
    }, (error) => {
      console.error("Gagal memuat ulasan produk:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Merge dynamic reviews with premium default reviews if less than 3
  const defaultReviews: UserReview[] = [
    { id: 'd1', userName: "Budi Santoso", productName: "Sertifikasi Badan Usaha SBU", rating: 5, reviewText: "Layanan SBU sangat cepat dan profesional. Sangat membantu untuk tender proyek pemerintah." },
    { id: 'd2', userName: "Siti Aminah", productName: "Aplikasi Laporan Keuangan Kontraktor", rating: 5, reviewText: "Aplikasi laporan keuangannya sangat mudah digunakan. Sekarang pembukuan proyek jadi lebih rapi." },
    { id: 'd3', userName: "Andi Wijaya", productName: "Template Excel RAB Konstruksi", rating: 5, reviewText: "Template RAB-nya sangat detail dan akurat. Menghemat waktu pengerjaan administrasi proyek kami." }
  ];

  // Display top 3 reviews
  const displayedReviews = reviews.length >= 3 
    ? reviews.slice(0, 3) 
    : [...reviews, ...defaultReviews.slice(0, 3 - reviews.length)];

  return (
    <motion.section 
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] as const }}
      className="py-24 bg-muted/50 overflow-hidden border-t border-border/50"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-40px' }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] as const }}
          className="text-center mb-16"
        >
          <span className="text-gold font-black uppercase tracking-[0.3em] text-sm mb-4 block">Ulasan & Kritik</span>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-4">Apa Kata <span className="text-gold">Klien</span> Kami</h2>
          <p className="text-foreground/60 max-w-xl mx-auto">Kepercayaan Anda adalah prioritas utama kami. Berikut adalah ulasan dari klien kami.</p>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {displayedReviews.map((t, i) => (
            <motion.div 
              key={t.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-30px' }}
              transition={{ duration: 0.5, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] as const }}
              whileHover={{ y: -6, transition: { duration: 0.2 } }}
              className="bg-card p-8 rounded-[2rem] shadow-xl shadow-foreground/5 border border-border flex flex-col justify-between h-full hover:border-gold/30 hover:shadow-2xl transition-all duration-300"
            >
              <div>
                <div className="flex text-gold gap-1 mb-4">
                  {[...Array(5)].map((_, idx) => (
                    <Star key={idx} className={`h-4 w-4 ${idx < t.rating ? 'text-gold fill-current' : 'text-foreground/10'}`} />
                  ))}
                </div>
                <p className="text-foreground/70 italic text-sm mb-6 leading-relaxed">
                  "{t.reviewText || 'Layanan luar biasa, responsif dan sangat direkomendasikan!'}"
                </p>
              </div>
              <div className="pt-4 border-t border-border/40">
                <p className="font-bold text-foreground text-sm leading-tight">{t.userName}</p>
                {t.productName && (
                  <p className="text-[10px] text-primary font-bold uppercase tracking-wider mt-1">{t.productName}</p>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.section>
  );
}

function Footer() {
  return (
    <footer className="bg-slate-950 text-slate-100 pt-24 pb-12 overflow-hidden relative border-t border-slate-800">
      {/* Decorative Ornaments */}
      <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-gold/5 to-transparent pointer-events-none"></div>
      <div className="absolute -top-24 -right-24 w-64 h-64 bg-gold/5 rounded-full blur-3xl"></div>
      <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-gold/5 rounded-full blur-3xl"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-16 mb-20">
          {/* Brand Identity */}
          <div className="md:col-span-5">
            <Link to="/" className="inline-block group mb-8">
              <h3 className="text-3xl font-black tracking-tighter flex items-center gap-2">
                <SiteLogo className="w-8 h-8 rounded-lg shadow-[0_0_20px_rgba(201,161,74,0.3)] group-hover:scale-110 transition-transform" />
                AZMAN<span className="text-gold group-hover:pl-1 transition-all">204</span>
              </h3>
            </Link>
            <p className="text-slate-100/40 max-w-sm leading-relaxed text-lg mb-10 font-medium italic">
              "Membangun Fondasi Digital untuk Masa Depan Konstruksi Indonesia."
            </p>
            <div className="flex items-center gap-3">
              {[
                { icon: Instagram, label: 'Instagram', href: 'https://www.instagram.com/azman_204' },
                { icon: Facebook, label: 'Facebook', href: 'https://www.facebook.com/azman204official' },
                { icon: Music, label: 'TikTok', href: 'https://tiktok.com/@ja_k_on' },
                { icon: Youtube, label: 'YouTube', href: 'https://www.youtube.com/@azman204.' }
              ].map((social) => (
                <a 
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-xl bg-slate-100/5 border border-slate-100/5 flex items-center justify-center text-slate-100/40 hover:text-gold hover:border-gold/50 hover:bg-gold/5 transition-all group"
                >
                  <social.icon className="w-5 h-5 group-hover:scale-110 transition-transform" />
                </a>
              ))}
            </div>
          </div>
          
          {/* Quick Links */}
          <div className="md:col-span-3">
            <h4 className="text-[10px] uppercase tracking-[0.4em] font-black text-gold/60 mb-10">Eksplorasi</h4>
            <ul className="space-y-5">
              {[
                { name: 'Beranda', path: '/' },
                { name: 'Layanan', path: '/services' },
                { name: 'Produk Digital', path: '/products' },
                { name: 'Tanya Jawab (FAQ)', path: '/#faq' },
                { name: 'Pusat Bantuan', path: '/help' }
              ].map((link) => (
                <li key={link.name}>
                  <Link to={link.path} className="text-slate-100/60 hover:text-slate-100 flex items-center justify-between group transition-colors font-semibold">
                    <span>{link.name}</span>
                    <ArrowUpRight className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-gold" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Cards */}
          <div className="md:col-span-4">
            <h4 className="text-[10px] uppercase tracking-[0.4em] font-black text-gold/60 mb-10">Hubungi Kami</h4>
            <div className="space-y-4">
              <a 
                href="mailto:azmankoe916@gmail.com" 
                className="flex items-center gap-4 p-4 rounded-2xl bg-slate-100/5 border border-slate-100/5 hover:border-gold/20 hover:bg-slate-100/[0.07] transition-all group"
              >
                <div className="w-10 h-10 rounded-xl bg-gold/10 flex items-center justify-center text-gold group-hover:scale-110 transition-transform">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-[10px] text-slate-100/30 uppercase font-black tracking-widest">Email</p>
                  <p className="text-sm font-bold">azmankoe916@gmail.com</p>
                </div>
              </a>
              <a 
                href="https://wa.me/6281261089400" 
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 p-4 rounded-2xl bg-slate-100/5 border border-slate-100/5 hover:border-gold/20 hover:bg-slate-100/[0.07] transition-all group"
              >
                <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center text-green-500 group-hover:scale-110 transition-transform">
                  <MessageCircle className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-[10px] text-slate-100/30 uppercase font-black tracking-widest">WhatsApp</p>
                  <p className="text-sm font-bold">+62 812 6108 9400</p>
                </div>
              </a>
              <div className="flex items-center gap-4 p-4 rounded-2xl bg-slate-100/5 border border-slate-100/5">
                <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center text-blue-500">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-[10px] text-slate-100/30 uppercase font-black tracking-widest">Lokasi</p>
                  <p className="text-sm font-bold">Jl. Soekarno-Hatta, Anambas, Riau</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="pt-12 border-t border-slate-100/5 flex flex-col items-center gap-8 text-center">
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-3 text-[10px] font-black uppercase tracking-[0.2em] text-slate-100/40 italic">
            <Link to="/sitemap" className="hover:text-gold transition-colors">Sitemap</Link>
            <Link to="/terms" className="hover:text-gold transition-colors">Syarat & Ketentuan</Link>
            <Link to="/privacy" className="hover:text-gold transition-colors">Kebijakan Privasi</Link>
          </div>
          <p className="text-slate-100/20 text-[10px] font-black uppercase tracking-[0.4em]">
            © 2024 AZMAN204 — SOLUTIONS
          </p>
        </div>
      </div>
    </footer>
  );
}

function AuthRouteHandler({ mode }: { mode: 'login' | 'register' }) {
  const { openAuthModal } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    openAuthModal(mode);
    navigate('/', { replace: true });
  }, [mode, openAuthModal, navigate]);

  return null;
}

function AppRoutes() {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, y: 10, filter: 'blur(3px)' }}
        animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
        exit={{ opacity: 0, y: -8, filter: 'blur(3px)' }}
        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] as const }}
        className="flex-1 w-full flex flex-col"
      >
        <Routes location={location}>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<AuthRouteHandler mode="login" />} />
          <Route path="/register" element={<AuthRouteHandler mode="register" />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/admin" element={<AdminPanel />} />
          <Route path="/product/:id" element={<ProductDetail />} />
          <Route path="/articles" element={<Articles />} />
          <Route path="/article/:slug" element={<ArticleDetail />} />
          <Route path="/regulations" element={<Regulations />} />
          <Route path="/education" element={<ConstructionEducation />} />
          <Route path="/education/:slug" element={<EducationDetail />} />
          <Route path="/services" element={<div className="pt-32"><Services /></div>} />
          <Route path="/services/:slug" element={<ServiceDetail />} />
          <Route path="/products" element={<div className="pt-32"><Products /></div>} />
          <Route path="/help" element={<HelpCenter />} />
          <Route path="/sitemap" element={<Sitemap />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <AuthProvider>
          <NotificationProvider>
            <Router>
              <ScrollToTopOnNavigate />
              <DynamicFavicon />
              <div className="min-h-screen bg-background text-foreground transition-colors duration-300 pb-16 md:pb-0 flex flex-col">
                <Navbar />
                <main className="flex-1 w-full flex flex-col">
                  <AppRoutes />
                </main>
                <BottomNav />
                <FloatingChatAdmin />
                <BackToTop />
                <PhoneRegistrationModal />
                <Toaster position="top-center" richColors />
              </div>
            </Router>
          </NotificationProvider>
        </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}
